import styled, { css } from 'styled-components'
import { belowTablet, belowPhone, darkMode } from 'utilities/styled'
import { CloseButton as CloseButtonBase } from 'components/button'
import { H1, ModalHeading, H3, H4 } from 'components/typography/heading'
import Paragraph, { paragraphSmallCss, captionCss } from 'components/typography/paragraph'

export const modalBorderShadow = css`
    box-shadow:
        0 -4px 16px 0 var(--huma-color-shadow),
        var(--huma-shadow-high);

    ${darkMode`
        box-shadow:
            inset 0 0 0 1px var(--huma-color-border-default),
            0 -4px 16px 0 var(--huma-color-shadow),
            var(--huma-shadow-high);
    `}
`

export const Wrapper = styled.dialog`
    --__huma-component-modal-content-padding: 32px;

    position: fixed;
    inset: 0;

    border: unset;
    max-width: unset;
    width: 100vw;
    max-height: unset;
    height: 100dvh;
    padding: 0;
    background-color: transparent;

    transition: all var(--__huma-component-modal-close-duration);

    &,
    & > div,
    &::backdrop {
        opacity: 0;
        transition-behavior: allow-discrete;
        transition-property: opacity, display, overlay;
        transition-duration: var(--__huma-component-modal-close-duration);
    }

    &::backdrop {
        --__huma-component-modal-backdrop-alpha: .2;

        background-color: rgb(from var(--huma-palette-neutral-10) r g b / var(--__huma-component-modal-backdrop-alpha));

        ${darkMode`
            --__huma-component-modal-backdrop-alpha: .4;
        `}
    }

    &.no-backdrop::backdrop {
        background-color: transparent !important;
    }

    &[open] {
        &,
        & > div,
        &::backdrop {
            opacity: 1;
        }
    }

    @starting-style {
        &,
        & > div,
        &[open]::backdrop {
            opacity: 0;
        }
    }
`

export const Container = styled.div`
    display: grid;
    grid-template: 1fr / 1fr;

    overflow-y: auto;
    position: inherit;
    inset: inherit;

    ${Wrapper}.prevent-scroll & {
        overflow: hidden;
    }

    ${Wrapper}:not(.fixed) & {
        place-items: center;
    }

    ${Wrapper}.fixed & {
        align-content: start;
    }
`

export const Content = styled.div`
    --__huma-component-modal-row-gap: 24px;

    display: grid;
    grid-template-rows: [body] var(--__huma-component-modal-content-row, 1fr);
    grid-template-columns:
        [fullbleed-start] var(--__huma-component-modal-content-padding)
        [content-start] minmax(0, 1fr) [content-end]
        var(--__huma-component-modal-content-padding) [fullbleed-end];
    row-gap: var(--__huma-component-modal-row-gap);

    margin: 10svh auto;
    height: max-content;

    transition: all var(--__huma-component-modal-close-duration);

    color: var(--huma-color-foreground-default);

    div {
        overscroll-behavior: none;
    }

    > * {
        grid-column: content;
    }

    &:has(.modal-header) {
        grid-template-rows:
            [header] auto
            [body] var(--__huma-component-modal-content-row, 1fr);
    }

    &:has(.modal-footer) {
        grid-template-rows:
            [body] var(--__huma-component-modal-content-row, 1fr)
            [footer] auto;
    }

    &:has(.modal-header):has(.modal-footer) {
        grid-template-rows:
            [header] auto
            [body] var(--__huma-component-modal-content-row, 1fr)
            [footer] auto;
    }

    ${Wrapper}.plain & {
        grid-template-columns:
            [fullbleed-start] 0
            [content-start] minmax(0, 1fr) [content-end]
            0 [fullbleed-end];
    }

    ${Wrapper}:where(:not(.plain)) & {
        border-radius: 8px;
        width: 400px;
        max-width: 90vw;
        max-height: 80svh;
        padding-block: var(--__huma-component-modal-content-padding);
        background-color: var(--huma-color-surface-default);
        ${modalBorderShadow}

        ${belowTablet`
            --__huma-component-modal-content-padding: 24px;

            margin-block: 0;
            max-width: calc(100vw - 16px);
            max-height: calc(100svh - 32px);
        `}
    }

    ${Wrapper}.fullscreen & {
        margin: 16px;
        width: calc(100vw - 32px);
        max-width: calc(100vw - 32px);
        height: calc(100vh - 32px);
        max-height: calc(100svh - 32px);

        ${belowTablet`
            margin: 32px 0 0;
            width: 100vw;
            max-width: 100vw;
            height: 100%;
            max-height: unset;
        `}

        ${belowPhone`
            border-radius: 16px;
        `}
    }

    ${Wrapper}.compact & {
        --__huma-component-modal-row-gap: 0;

        padding-block: 0;
    }

    ${Wrapper}.no-translate & {
        transform: unset;
    }

    ${Wrapper}[open] & {
        transform: translateY(0);
        opacity: 1;
        transition:
            transform calc(var(--__huma-component-modal-open-duration) * 2.5) linear(0, .007, .029 2.2%, .118 4.7%, .625 14.4%, .826 19%, .902, .962, 1.008 26.1%, 1.041 28.7%, 1.064 32.1%, 1.07 36%, 1.061 40.5%, 1.015 53.4%, .999 61.6%, .995 71.2%, 1),
            opacity var(--__huma-component-modal-open-duration);

        @media (prefers-reduced-motion: reduce) {
            transition: opacity var(--__huma-component-modal-open-duration);
        }
    }

    @starting-style {
        ${Wrapper}[open] & {
            opacity: 0;

            @media (prefers-reduced-motion: no-preference) {
                transform: translateY(calc(100vh / 3));
            }
        }

        ${Wrapper}[open].no-translate & {
            transform: unset;
        }
    }

    ${Wrapper}.scrollable & {
        margin-block: 32px;
        max-height: unset;

        ${belowTablet`
            margin-block: 8px;
        `}
    }

    ${Wrapper}.fixed & {
        margin-block: var(--__modal-content-margin, 80px) calc(var(--__modal-content-margin, 80px) / 2);

        ${belowTablet`
            --__modal-content-margin: 8px;

            margin-block: var(--__modal-content-margin);
        `}
    }

    ${Wrapper}.medium & {
        max-width: calc(100vw - 16px);
        width: 808px;
    }

    ${Wrapper}.large & {
        margin-inline: auto;
        max-width: calc(100vw - 16px);
        width: 1048px;

        ${belowTablet`
            max-width: 100vw;
        `}
    }

    &:has(.modal-body) > form {
        display: grid;
        grid-template-rows: subgrid;
        grid-row: body / span 2;
    }
`

export const Huge = styled(H1)`
    margin-block-end: 0;

    line-height: 40px;

    ${belowTablet`
        font-size: 24px;
        line-height: 32px;
    `}
`

export const Big = styled(ModalHeading)`
    margin-block-end: 0;
`

export const Medium = styled(H3)`
    margin-block-end: 0;

    line-height: 24px;
`

export const Small = styled(H4)`
    margin-block-end: 0;
`

export const Kicker = styled(Paragraph)`
    ${captionCss}

    margin-block-end: 4px;
`

export const ModalHeader = styled.header`
    min-width: 0;

    ${Wrapper}.wrap-header & {
        position: relative;

        align-items: center;

        &::after {
            content: "";
            position: absolute;
            inset-block-end: 0;
            inset-inline: calc(var(--__huma-component-modal-content-padding) * -1);

            border-bottom: 1px solid var(--huma-color-border-default);
        }

        &.fullbleed::after {
            inset-inline: 0;
        }
    }

    ${Wrapper}.compact & {
        padding-block: var(--__huma-component-modal-content-padding);
    }

    &.flipped {
        display: flex;
        align-items: center;
        column-gap: 24px;
    }

    &.fullbleed {
        grid-column: fullbleed;
    }

    &.baseline-close {
        display: flex;
        justify-content: space-between;
        flex-direction: row-reverse;

        & > .modal-header-children {
            flex-grow: 1;
        }
    }

    & .modal-header-symbol {
        margin-block-end: 16px;
    }

    &:has(> button) .modal-header-content {
        align-items: start;
        justify-content: space-between;
    }

    & .modal-header-heading:not(:last-child) {
        margin-block-end: 4px;
    }

    & .modal-header-heading:has(+ .modal-header-caption) {
        margin-block-end: 8px;
    }

    & .modal-header-heading ~ .modal-header-children {
        clear: both;
    }

    & .modal-header-children > :last-child {
        margin-block-end: 0;
    }
`

export const CloseButton = styled(CloseButtonBase)`
    position: static;

    float: right;

    margin-block-end: -10px;
    margin-inline-start: 24px;

    transform: translateY(-6px);

    ${ModalHeader} :where(h3, h4) ~ & {
        margin-block-end: -16px;

        transform: translateY(-8px);
    }

    ${ModalHeader}.baseline-close & {
        align-self: baseline;
        flex-shrink: 0;

        margin-block-end: 0;

        transform: translateY(0);
    }

    ${belowTablet`
        ${Wrapper}.wrap-header & {
            align-self: start;
        }
    `}
`

export const Caption = styled(Paragraph)`
    grid-row: caption;

    ${Small} + & {
        ${paragraphSmallCss}
    }
`

export const ModalBody = styled.article`
    position: relative;

    min-width: 0;

    > :last-child,
    [data-focus-lock-disabled] > :last-child {
        margin-block-end: 0;
    }

    &.scrollable {
        max-height: 100%;
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    &.fullbleed {
        grid-column: fullbleed;

        &.with-padding {
            padding-inline: var(--__huma-component-modal-content-padding);
        }
    }

    ${Wrapper}.fullscreen &:only-child {
        height: inherit;
    }

    ${Wrapper}.compact & {
        padding-block: var(--__huma-component-modal-content-padding);

        &.compact {
            padding-block: 0;
        }
    }

    &.compact-end,
    ${Wrapper}.compact &.compact-end {
        padding-block-end: 0;
    }
`

export const ModalFooter = styled.footer`
    min-width: 0;

    > :first-child {
        margin-block-start: 0;
    }

    &.fullbleed {
        grid-column: fullbleed;
    }

    ${Wrapper}.wrap-footer & {
        position: relative;

        align-items: center;

        &::after {
            content: "";
            position: absolute;
            inset-block-start: 0;
            inset-inline: calc(var(--__huma-component-modal-content-padding) * -1);

            border-bottom: 1px solid var(--huma-color-border-default);
        }

        &.fullbleed::after {
            inset-inline: 0;
        }
    }

    ${Wrapper}.compact & {
        padding-block: var(--__huma-component-modal-content-padding);

        &.compact {
            padding-block: 0;
        }
    }
`

export const LoadingContainer = styled.div`
    position: relative;

    height: 80px;
`